import { z } from 'zod';
import type { HolidayScheme, WorkloadScheme } from '@/components/common/settings/capacity/types';
import type { LocationSummary, Resource, ResourceType } from './capacity/resources';
import type { Skill } from './skills';

type TeamSourceType = 'JIRA_TEAM' | 'TEMPO_TEAM';

export interface CreateTeamRequest {
  name: string;
}

export interface Team extends CreateTeamRequest {
  id: number;
  icon?: string;
  color: string;
  membershipCount?: number;
  totalCapacitySeconds: number | null;
  createdAt: string;
  updatedAt: string;
  description: string | null;
  sourceType?: TeamSourceType;
  instanceId?: string;
  lastSyncedAt?: string;
  memberships?: TeamMembershipWithResource[];
}

export type TeamMembership = {
  membershipId: number;
  startDate?: string;
  endDate?: string;
  commitment?: number;
};

export type ResourceTeamMembership = {
  membershipId: number;
  resourceId: number;
  teamId: number;
  teamName: string;
  startDate?: string;
  endDate?: string;
  commitment?: number;
  roleId?: number;
  roleName?: string;
};

export type PendingMembershipChanges = {
  [membershipId: number]: Partial<MembershipUpdateRequest>;
};

export type MembershipUpdateRequest = {
  startDate?: string | null;
  endDate?: string | null;
  commitment?: number;
  roleId?: number | null;
};

export type MembershipBulkUpdateItem = {
  membershipId: number;
  update: MembershipUpdateRequest;
};

export type MembershipBulkUpdateRequest = {
  updates: MembershipBulkUpdateItem[];
};

export type TeamMembershipRequest = MembershipUpdateRequest & {
  teamId: number;
};

export type MembershipBulkAddRequest = {
  memberships: TeamMembershipRequest[];
};

export type MembershipBulkDeleteRequest = {
  membershipIds: number[];
};

export type MembershipBulkOperation = {
  resourceIds: number[];
  teamIds: number[];
};

export type BulkTeamActionRequest = {
  membershipsToAdd?: MembershipBulkOperation;
  membershipsToRemove?: MembershipBulkOperation;
};

export type MembershipResponse = {
  id: number;
  resourceId: number;
  teamId: number;
  startDate?: string;
  endDate?: string;
  commitment?: number;
};

export type BulkTeamActionResponse = {
  membershipsCreated: MembershipResponse[];
  membershipsUpdated: MembershipResponse[];
  membershipsDeleted: number[];
};

export type TeamMemberWithCapacity = {
  resourceId: number;
  resourceType: ResourceType;
  resourceName: string;
  resourceStatus: ResourceStatus;
  resourceSourceId?: string;
  capacitySeconds?: number;
  memberships: TeamMembership[];
};

export interface TeamLookupResponse {
  id: number;
  name: string;
  description: string | null;
  totalCapacitySeconds: number | null;
  members?: TeamMemberWithCapacity[];
}

export type TeamsLookupResponse = {
  items: TeamLookupResponse[];
};

export type TeamsResponse = {
  items: Team[];
};

export interface TeamResponse {
  data: Team;
}

export type WorkspaceUserGroup = {
  id: number;
  group: {
    id: number;
  };
};

export type WorkspaceUserGroupsById = Record<string, WorkspaceUserGroup>;

export interface TeamsWithIncludedResponse {
  data: Team[];
  included?: {
    workspaceUserGroups: WorkspaceUserGroupsById;
  };
}
export type StatusAction = 'DEACTIVATE' | 'REACTIVATE';
export const STATUS_ACTION = {
  DEACTIVATE: 'DEACTIVATE' as StatusAction,
  REACTIVATE: 'REACTIVATE' as StatusAction,
};

export type ResourceStatus = 'ACTIVE' | 'DEACTIVATED';
export const RESOURCE_STATUS = {
  ACTIVE: 'ACTIVE' as ResourceStatus,
  DEACTIVATED: 'DEACTIVATED' as ResourceStatus,
};
export type ResourceFilterStatus = ResourceStatus | 'ALL';

export type SearchMembershipsSearchParams = {
  query?: string;
  resourceStatus?: ResourceFilterStatus;
  roleId?: number[];
  skillId?: number[];
  locationId?: number[];
  showPastMemberships?: boolean;
  showFutureMemberships?: boolean;
};

export type TeamRoleSummary = {
  id: number;
  name: string;
};

export type TeamMembershipWithResource = {
  membershipId: number;
  resourceId: number;
  resourceType: ResourceType;
  resourceName: string;
  resourceStatus: ResourceStatus;
  resourceSourceId?: string;
  membershipSourceId?: string;
  otherTeams?: Team[];
  workloadSchemes?: WorkloadScheme[];
  holidaySchemes?: HolidayScheme[];
  startDate?: string;
  endDate?: string;
  commitment?: number;
  location?: LocationSummary | null;
  role?: TeamRoleSummary;
  skills?: Skill[];
};

export interface TeamWithIncludedResponse {
  team: Team;
  memberships?: TeamMembershipWithResource[];
}

export type GetTeamsParams = { includeMembers?: boolean };

export const CREATE_MODE = 'CREATE';
export const EDIT_MODE = 'EDIT';
export type TeamFormMode = typeof CREATE_MODE | typeof EDIT_MODE;

export interface TeamRequestWithMembers extends CreateTeamRequest {
  members?: Resource[];
}

export interface ImportTeamsSearch {
  sourceId: string;
  name: string;
  imported: boolean;
}

export interface ImportTeamsSearchResponse {
  items: ImportTeamsSearch[];
}

export interface ImportTeamsSearchParameters {
  sourceType: TeamSourceType;
  credentialsId: number;
  instanceId: string;
}

export interface ImportTeamsRequest {
  sourceType: TeamSourceType;
  credentialsId: number;
  instanceId: string;
  teams: ImportTeamsSearch[];
}

export interface DateRangeInput {
  from: string;
  to: string;
}

export interface TeamsLookupRequest {
  ids: number[];
  dateRange: DateRangeInput;
}

export interface SyncTeamsRequest {
  teamType: TeamSourceType;
  oauthCredentialsId: number;
  instanceId: string;
}

type SyncStatusType = 'JIRA_TEAM' | 'TEMPO_TEAM' | 'RESOURCE';

type SyncStatusStatus = 'PENDING' | 'IN_PROGRESS' | 'DONE' | 'FAILED';

export interface SyncStatusResponse {
  items: SyncStatus[];
}

export interface SyncStatus {
  instanceId: string;
  type: SyncStatusType;
  status: SyncStatusStatus;
}

export const MEMBERS = 'MEMBERS' as const;
export const TEAM = 'TEAM' as const;
export const SKELETON = 'SKELETON' as const;
export type TeamSkeletonRow = { type: typeof SKELETON; id: string };
export type TeamRow = { type: typeof TEAM; team: Team; isSyncing: boolean } | TeamSkeletonRow;
export type TeamsPageTabType = typeof MEMBERS | typeof TEAM;

export type ResourceFormData = {
  name: string;
};

export interface PendingNewMembership {
  teamId: number;
  teamName: string;
  tempId: string;
  commitment?: number;
  startDate?: string | null;
  endDate?: string | null;
  roleId?: number | null;
}

export const TEAM_SORT_FIELD = {
  ID: 'ID',
  NAME: 'NAME',
  DESCRIPTION: 'DESCRIPTION',
  CREATED_AT: 'CREATED_AT',
  UPDATED_AT: 'UPDATED_AT',
  MEMBERSHIP_COUNT: 'MEMBERSHIP_COUNT',
} as const;

export type TeamSortField = (typeof TEAM_SORT_FIELD)[keyof typeof TEAM_SORT_FIELD];

export const SORT_DIRECTION = {
  ASC: 'ASC',
  DESC: 'DESC',
} as const;

export type SortDirection = (typeof SORT_DIRECTION)[keyof typeof SORT_DIRECTION];

export type TeamSearchParams = {
  query?: string;
  sortBy?: TeamSortField;
  sortDirection?: SortDirection;
  limit?: number;
  cursor?: string;
  roleIds?: number[];
};

export const membershipOverlapConflictSchema = z.object({
  resourceId: z.number(),
  existingStartDate: z.string().nullable(),
  existingEndDate: z.string().nullable(),
});

export type MembershipOverlapConflict = z.infer<typeof membershipOverlapConflictSchema>;

export type TeamsPageResponse = {
  items: Team[];
  recordLimit: number | null;
  recordCount: number;
  cursor: string | null;
  hasNextPage: boolean;
};
